import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import AuthCard from '@/components/auth/AuthCard'
import ImageSection from '@/components/auth/ImageSection'
import { useForgotPassword } from '@/hooks/authHooks'
import { resolveAuth } from '@/lib/auth'
import { useState, useEffect } from 'react'

const forgotSchema = z.object({
  email: z.string().email('البريد الإلكتروني غير صالح'),
})

export const Route = createFileRoute('/_regular/forgot-password/')({
  component: ForgotPasswordPage,
  beforeLoad: async ({ context: { queryClient } }) => {
    const auth = await resolveAuth(queryClient)
    if (auth.isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
})

function ForgotPasswordPage() {
  const forgotPassword = useForgotPassword()
  const [email, setEmail] = useState('')
  const [cooldown, setCooldown] = useState(0)

  const { register, handleSubmit, formState: { errors }, getValues } = useForm({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  })

  // Cooldown timer effect
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const onSubmit = async (data) => {
    setEmail(data.email)
    forgotPassword.mutate(data.email, {
      onSuccess: () => {
        // Start cooldown (e.g., 60 seconds) after successful send
        setCooldown(60)
        forgotPassword.reset() // reset error/success state but we keep success view
      },
    })
  }

  const handleResend = () => {
    if (cooldown > 0 || forgotPassword.isPending) return
    forgotPassword.mutate(email, {
      onSuccess: () => {
        setCooldown(60)
        forgotPassword.reset()
      },
    })
  }

  const handleBackToForm = () => {
    forgotPassword.reset() // clears success/error states
    setCooldown(0)
  }

  // Success state after email sent
  if (forgotPassword.isSuccess) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex flex-col md:flex-row">
          <div className="relative overflow-hidden order-2 md:order-1 w-full md:w-1/2 flex items-center justify-center p-6 md:p-12">
            <AuthCard title="استعادة كلمة المرور" description="تم إرسال رابط إعادة التعيين">
              <div className="text-center space-y-4">
                <div className="text-green-600">
                  تم إرسال رابط إعادة التعيين إلى <strong>{email}</strong>.
                </div>
                <div className="text-sm text-muted-foreground">
                  لم تستلم البريد؟ تحقق من مجلد البريد العشوائي أو أعد الإرسال.
                </div>
                <div className="flex flex-col gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={handleResend}
                    disabled={cooldown > 0 || forgotPassword.isPending}
                    className="w-full"
                  >
                    {cooldown > 0
                      ? `إعادة الإرسال بعد ${cooldown} ثانية`
                      : forgotPassword.isPending
                      ? 'جاري الإرسال...'
                      : 'إعادة إرسال الرابط'}
                  </Button>
                  <Button variant="ghost" onClick={handleBackToForm} className="w-full">
                    استخدام بريد إلكتروني آخر
                  </Button>
                  <Link to="/login" className="text-primary hover:underline text-sm">
                    العودة إلى تسجيل الدخول
                  </Link>
                </div>
              </div>
            </AuthCard>
          </div>
          <div className="order-1 md:order-2 w-full md:w-1/2 h-64 md:h-auto">
            <ImageSection />
          </div>
        </div>
      </div>
    )
  }

  // Normal form state
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col md:flex-row">
        <div className="relative overflow-hidden order-2 md:order-1 w-full md:w-1/2 flex items-center justify-center p-6 md:p-12">
          <AuthCard title="استعادة كلمة المرور" description="سنرسل رابط إعادة التعيين إلى بريدك الإلكتروني">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label>البريد الإلكتروني</Label>
                <Input
                  type="email"
                  {...register('email')}
                  autoComplete="email"
                  disabled={forgotPassword.isPending}
                />
                {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
              </div>
              {forgotPassword.isError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {forgotPassword.error?.response?.data?.message || 'حدث خطأ، يرجى المحاولة لاحقاً.'}
                  </AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={forgotPassword.isPending}>
                {forgotPassword.isPending ? 'جاري الإرسال...' : 'إرسال رابط إعادة التعيين'}
              </Button>
            </form>
          </AuthCard>
        </div>
        <div className="order-1 md:order-2 w-full md:w-1/2 h-64 md:h-auto">
          <ImageSection />
        </div>
      </div>
    </div>
  )
}